import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';

import { formatHelpDate } from '../help/helpDate';
import type { HelpEntry } from '../help/helpEntries';
import { useLocale } from '../i18n/LocaleContext';
import { RADIUS, SPACE } from '../theme/spacing';
import { TYPE, weight } from '../theme/typography';
import { hapticLight } from '../util/haptics';
import { useEventCallback } from '../util/useEventCallback';

/**
 * Цвета ящика подсказок. Собираются вызывающим экраном из палитры темы — см. `ChatListScreen`.
 *
 * <p>Отдельный тип, а не `ChatListPalette` целиком: окну нужны тринадцать цветов из полутора
 * сотен, и узкий контракт сразу показывает, что именно красится.
 */
export type HelpCenterPalette = {
  /** Затемнение под карточкой. */
  backdrop: string;
  card: string;
  cardBorder: string;
  title: string;
  /** Подписи-пояснения над списком и под ним. */
  hint: string;
  /** Запись списка: заливка, рамка, заголовок, дата, текст абзацев. */
  entry: string;
  entryBorder: string;
  entryTitle: string;
  entryMeta: string;
  entryBody: string;
  chevron: string;
  newBadge: string;
  newBadgeText: string;
};

type Props = {
  visible: boolean;
  onClose: () => void;
  entries: readonly HelpEntry[];
  /** Идентификаторы, которые были непрочитанными в момент открытия — для значка «Новое». */
  unseenIds: readonly string[];
  onMarkAllRead: () => void;
  palette: HelpCenterPalette;
};

/**
 * «Подсказки и новости» — ящик, который открывает лампочка в шапке списка чатов
 * (заказ владельца 2026-08-31).
 *
 * <p><b>Что решает.</b> Половина возможностей приложения ничем себя не объявляет: чек-лист
 * получается из строки с плюсом, у заметки есть закрепление и цвет, у сообщения — отложенная
 * отправка. Рассказать об этом было негде — подсказку внутри экрана видит только тот, кто на
 * этот экран уже дошёл. Тот же ящик служит и объявлением о новом: запись встаёт наверх,
 * лампочка мигает, человек читает — лампочка гаснет.
 *
 * <p><b>Почему записи свёрнуты.</b> Развёрнутые полдесятка статей — это экран сплошного текста,
 * который пролистывают не читая. Свёрнутый список читается как оглавление: видно, о чём тут
 * вообще говорят, и открывается ровно то, что нужно. Непрочитанная запись при этом открыта
 * сразу — ради неё лампочка и мигала.
 *
 * <p><b>Почему «прочитано» ставится на открытии, а не по кнопке.</b> Отдельная кнопка «я
 * прочитал» — лишний шаг, который забывают нажать, и лампочка продолжает мигать у человека,
 * который всё прочёл. Открыл ящик — значит увидел; ради этого лампочка и звала.
 *
 * <p><b>Почему цвета приходят снаружи.</b> Раньше окно брало оформление у органайзера
 * (`organizerModalStyles`) — тёмную карточку, одинаковую во всех темах. У напоминания или
 * отложенной отправки такой довод есть: они открываются поверх переписки, под которой лежат
 * обои, и спорить с фотографией на фоне им нечем. Здесь фона нет: ящик зовут из списка чатов,
 * и тёмная плита посреди светлой темы читалась как окно из чужого приложения. Поэтому цвета
 * берутся из палитры темы, как у остальных окон этого экрана.
 */
export function HelpCenterModal({
  visible,
  onClose,
  entries,
  unseenIds,
  onMarkAllRead,
  palette,
}: Props): React.ReactElement {
  const { t, locale } = useLocale();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const unseen = useMemo(() => new Set(unseenIds), [unseenIds]);
  const s = useMemo(() => stylesFor(palette), [palette]);

  // Намеренно только по открытию: `unseen` меняется вызовом `onMarkAllRead` ниже, и выбор
  // по нему свернул бы запись обратно тем же кадром.
  const [prevVisible, setPrevVisible] = useState(false);
  if (prevVisible !== visible) {
    setPrevVisible(visible);
    if (visible) {
      // Открыта самая свежая непрочитанная, а если непрочитанных нет — самая свежая вообще:
      // список, у которого всё свёрнуто, выглядит пустым.
      const firstUnseen = entries.find((e) => unseen.has(e.id));
      setExpandedId(firstUnseen?.id ?? entries[0]?.id ?? null);
    }
  }

  // «Прочитано» — раз на открытие; новая функция от родителя поводом пометить снова не служит.
  const markAllRead = useEventCallback(onMarkAllRead);
  useEffect(() => {
    if (visible) markAllRead();
  }, [visible, markAllRead]);

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={s.root}>
        <Pressable
          style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}
          onPress={onClose}
        />
        <View style={s.card}>
          <View style={s.headerRow}>
            <Text style={s.title}>{t('helpCenterTitle')}</Text>
            {/* Крестик — зона 44 pt (аудит 2026-10-04, п. 16): одна буква «×» была целью в 20 pt. */}
            <Pressable
              onPress={onClose}
              hitSlop={8}
              style={s.closeHit}
              accessibilityRole="button"
              accessibilityLabel={t('close')}>
              <Text style={s.title}>×</Text>
            </Pressable>
          </View>
          <Text style={s.hint}>{t('helpCenterIntro')}</Text>
          <ScrollView style={s.list} showsVerticalScrollIndicator={false}>
            {entries.map((entry) => {
              const open = expandedId === entry.id;
              return (
                <View key={entry.id} style={s.listItem}>
                  <Pressable
                    onPress={() => {
                      hapticLight();
                      setExpandedId(open ? null : entry.id);
                    }}
                    accessibilityRole="button"
                    accessibilityState={{ expanded: open }}
                    accessibilityLabel={entry.title[locale]}
                    style={s.titleRow}>
                    <Text style={s.icon}>{entry.icon}</Text>
                    <View style={s.titleColumn}>
                      <Text style={s.listItemTitle}>{entry.title[locale]}</Text>
                      {/* Закреплённая — без даты: «С чего начать» не новость, а вход. */}
                      <Text style={s.listItemMeta}>
                        {entry.pinned ? t('helpStartHereBadge') : formatHelpDate(entry.date, locale)}
                      </Text>
                    </View>
                    {unseen.has(entry.id) ? (
                      <View style={s.newBadge}>
                        <Text style={s.newBadgeText}>{t('helpCenterNewBadge')}</Text>
                      </View>
                    ) : null}
                    <Text style={s.chevron}>{open ? '▴' : '▾'}</Text>
                  </Pressable>
                  {open ? (
                    <View style={s.body}>
                      {entry.body[locale].map((paragraph, index) => (
                        <Text key={index} style={s.paragraph}>
                          {paragraph}
                        </Text>
                      ))}
                    </View>
                  ) : null}
                </View>
              );
            })}
          </ScrollView>
          <Text style={s.hint}>{t('helpCenterAllReadHint')}</Text>
        </View>
      </View>
    </Modal>
  );
}

function stylesFor(p: HelpCenterPalette) {
  return StyleSheet.create({
    root: {
      flex: 1,
      backgroundColor: p.backdrop,
      justifyContent: 'center',
      paddingHorizontal: SPACE.lg,
    },
    card: {
      backgroundColor: p.card,
      borderRadius: RADIUS.lg,
      borderWidth: 1,
      borderColor: p.cardBorder,
      padding: SPACE.lg,
      gap: SPACE.md,
      maxWidth: 560,
      width: '100%',
      alignSelf: 'center',
      // Потолок обязателен: карточка отцентрована по вертикали, и без него длинный список
      // растягивает её на весь экран.
      maxHeight: '88%',
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: SPACE.sm,
    },
    title: { ...TYPE.headline, color: p.title },
    closeHit: { minWidth: 44, minHeight: 44, alignItems: 'center', justifyContent: 'center' },
    hint: { ...TYPE.caption, color: p.hint },
    list: { flexGrow: 0, flexShrink: 1 },
    listItem: {
      backgroundColor: p.entry,
      borderRadius: RADIUS.md,
      borderWidth: 1,
      borderColor: p.entryBorder,
      padding: SPACE.md,
      gap: SPACE.xs,
      marginBottom: SPACE.sm,
    },
    titleRow: { flexDirection: 'row', alignItems: 'center', gap: SPACE.sm },
    icon: { fontSize: 20 },
    titleColumn: { flex: 1, gap: SPACE.hair },
    listItemTitle: { ...weight('body', '600'), color: p.entryTitle },
    listItemMeta: { ...TYPE.caption, color: p.entryMeta },
    chevron: { color: p.chevron, fontSize: 14 },
    newBadge: {
      backgroundColor: p.newBadge,
      borderRadius: RADIUS.pill,
      paddingHorizontal: SPACE.sm,
      paddingVertical: 3,
    },
    newBadgeText: { ...weight('micro', '700'), color: p.newBadgeText },
    body: { gap: SPACE.sm, paddingTop: SPACE.sm },
    /**
     * Междустрочный интервал берётся из шкалы (`TYPE.footnote`): абзацы здесь длинные, а
     * умолчание React Native зависит от системного шрифта — на одном телефоне текст
     * читаемый, на другом слипшийся.
     */
    paragraph: { ...TYPE.footnote, color: p.entryBody },
  });
}
